import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';
import { Search, MapPin, Building, Sparkles, ArrowUpRight } from 'lucide-react';

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('All');

  const formatIndianPrice = (amount) => {
    if (!amount && amount !== 0) return '';
    const num = Number(amount);
    if (isNaN(num)) return amount;
    
    if (num >= 10000000) {
      const cr = num / 10000000;
      return `${cr % 1 === 0 ? cr.toFixed(0) : cr.toFixed(1)} Cr.`;
    } else if (num >= 100000) {
      const lakh = num / 100000;
      return `${lakh % 1 === 0 ? lakh.toFixed(0) : lakh.toFixed(1)} Lakh`;
    } else {
      return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
    }
  };

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "properties"));
        const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProperties(list);
      } catch (err) {
        console.error("Error fetching properties:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  const filteredProperties = properties.filter(prop => {
    const matchesSearch = 
      (prop.title && prop.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (prop.location && prop.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (prop.builder && prop.builder.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = selectedType === 'All' || prop.type === selectedType;

    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center font-medium">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500 text-sm tracking-wide font-medium">Curating luxury properties...</p>
        </div>
      </div>
    );
  }

  const propertyTypes = ['All', '1 BHK Flat', '2 BHK Flat', '3 BHK Flat', 'Luxury Villa', 'Penthouse'];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans pb-20">
      
      {/* Hero Banner Section */}
      <div className="relative overflow-hidden bg-slate-900 py-20 px-4 md:px-8 mb-12 shadow-xl">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30 transform scale-105"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80')` }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent"></div>

        <div className="relative max-w-7xl mx-auto text-center z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full text-xs font-semibold uppercase tracking-wider mb-5 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" /> Premium Real Estate Portfolio
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold mb-4 tracking-tight text-white drop-shadow-sm">
            Discover Your Signature Residence
          </h1>
          <p className="text-slate-200 text-base md:text-lg max-w-2xl mx-auto mb-10 font-light leading-relaxed">
            Explore carefully curated luxury apartments, architectural villas, and exclusive penthouses with absolute zero brokerage.
          </p>

          {/* Search Bar inside Banner */}
          <div className="max-w-2xl mx-auto bg-white/95 backdrop-blur-xl p-2.5 rounded-2xl border border-slate-200 shadow-2xl flex items-center gap-2 transition-all">
            <div className="flex items-center pl-3 text-indigo-600">
              <Search className="w-5 h-5" />
            </div>
            <input 
              type="text"
              placeholder="Search by city, location, title, or elite builder..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-transparent px-2 py-2.5 text-slate-900 placeholder-slate-400 focus:outline-none text-sm md:text-base font-medium"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="text-xs text-slate-600 hover:text-slate-900 px-3.5 py-2 bg-slate-100 rounded-xl transition font-medium"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Filter Tabs Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Exclusive Portfolio</h2>
            <p className="text-slate-500 text-sm mt-1 font-normal">Showing {filteredProperties.length} elite properties available</p>
          </div>

          {/* Type Tabs */}
          <div className="flex items-center gap-2.5 overflow-x-auto pb-2 scrollbar-none">
            {propertyTypes.map((type) => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={`px-4 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all duration-300 shadow-sm ${
                  selectedType === type 
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30' 
                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Properties Grid */}
        {filteredProperties.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-3xl p-16 text-center max-w-lg mx-auto my-12 shadow-sm">
            <Building className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">No properties matched</h3>
            <p className="text-slate-500 text-sm mb-8 font-normal">We couldn't find any listings matching your search or selected filter options.</p>
            <button 
              onClick={() => { setSearchTerm(''); setSelectedType('All'); }}
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold rounded-xl transition shadow-md shadow-indigo-600/20"
            >
              Reset All Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProperties.map(prop => (
              <div 
                key={prop.id} 
                className="bg-white border border-slate-200/80 rounded-3xl overflow-hidden shadow-xl shadow-slate-200/50 flex flex-col hover:border-indigo-200 hover:shadow-2xl transition-all duration-300 group"
              >
                {/* Image Container */}
                <div className="relative h-60 w-full overflow-hidden">
                  <img 
                    src={prop.image || "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2"} 
                    alt={prop.title} 
                    className="h-full w-full object-cover group-hover:scale-105 transition duration-700 ease-out" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent"></div>
                  
                  <span className="absolute top-4 left-4 text-xs bg-indigo-600/95 backdrop-blur-md text-white px-3 py-1.5 rounded-xl font-semibold shadow-md">
                    {prop.type}
                  </span>

                  {prop.furnishing && (
                    <span className="absolute top-4 right-4 text-[11px] bg-white/90 backdrop-blur-md text-slate-800 px-3 py-1.5 rounded-xl font-semibold shadow-sm">
                      {prop.furnishing}
                    </span>
                  )}
                </div>
                
                {/* Details Container */}
                <div className="p-6 flex flex-col flex-grow justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 line-clamp-1 group-hover:text-indigo-600 transition">
                      {prop.title}
                    </h3>
                    
                    <p className="text-sm text-slate-600 mt-1.5 flex items-center gap-1.5 font-normal">
                      <MapPin className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                      <span className="line-clamp-1">{prop.location}</span>
                    </p>

                    {prop.builder && (
                      <p className="text-xs text-indigo-600 mt-1 font-semibold tracking-wide">By {prop.builder}</p>
                    )}
                    
                    {/* Extra Badges (Area & Bathrooms) */}
                    <div className="flex flex-wrap gap-2 mt-5 text-xs text-slate-600">
                      {prop.area && (
                        <span className="bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-xl font-medium">
                          📐 {prop.area}
                        </span>
                      )}
                      {prop.bathrooms && (
                        <span className="bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-xl font-medium">
                          🚿 {prop.bathrooms} Baths
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Price and Action Button */}
                  <div className="mt-8 pt-4 border-t border-slate-100 flex justify-between items-center gap-2">
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold block mb-0.5">Offered Price</span>
                      <span className="text-xl font-extrabold text-emerald-600 tracking-tight">
                        ₹ {formatIndianPrice(prop.price)}
                      </span>
                    </div>
                    <Link 
                      to={`/property/${prop.id}`} 
                      className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 rounded-xl text-xs font-semibold transition-all duration-300 shadow-md shadow-indigo-600/20 hover:scale-105 shrink-0"
                    >
                      View Details <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}